/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  gaQuestionCompletedEvent,
  handleNewQuizDataRaw,
  // IQuizData,
  QuizQuestionContext,
  SCREEN_QUIZ_QUESTION,
} from '../../../contextAPI'
import { API, IOption, ISubmitQuizQuestionPayload, ISubmitQuizQuestionResponse } from 'src/types'
import { EVENTS_FB_PIXEL, fbEvent, PATH_API, PATH_URL, validate } from 'src/helpers'
import { getEmailTypoSuggestion, isKnownTld } from 'src/helpers/validation'
import { SESSION_NAME_LOCAL, useAppContext } from 'src/helpers/app'
import { QuizType, REFERRAL_SOURCES } from 'src/pages/shared'
import { postHttp } from 'src/api'
import { ACTION_APP, ga4EventApp, gaEventApp } from 'src/helpers/googleAnalyticsService'
import { SegmentAction, segmentTracking, segmentTrackingBeacon } from 'src/helpers/segment'
import { vwoEvent } from 'src/helpers/vwo'
import { ENV } from 'src/config/env'
import { isValidPhoneNumber } from 'src/common'
import { tiktokEvent, EVENTS_TIKTOK_PIXEL, tiktokIdentify } from 'src/helpers/tiktokPixelService'
import { bingIdentify, bingTrackConversion } from 'src/helpers/bingAdsService'
import { createHash } from 'crypto'
import { QuizResponse } from 'src/types'
import { toast } from 'react-toastify'

interface formError {
  emailAddress?: boolean
  phoneNumber?: boolean
}

interface GuineaPigConfigValue {
  enabled: boolean
  budget: {
    enabled: boolean
    min: number
    max: number
  }
  colorPalette: {
    enabled: boolean
    colorPaletteIds: number[]
  }
  planningStage: {
    enabled: boolean
    planningStages: string[]
  }
}

// const submitQuizResponseAPI = (submitData: ISubmitQuizQuestionPayload) => {
//   return postHttp(PATH_API.postSubmit, submitData)
// }

const submitLastQuizResponseAPI = (submitData: ISubmitQuizQuestionPayload) => {
  return postHttp(PATH_API.postSubmitLastStep, submitData)
}

const MAX_TIME_TRACKING = 1500
const MAX_TIME_GOOGLE_TRACKING = 3000

const getShowGuineaPig = (
  budget: number,
  planningStage: string,
  paletteId: number,
  config: GuineaPigConfigValue | undefined
) => {
  if (!config || !config.enabled) return false

  return (
    (!config.budget.enabled || (budget >= config.budget.min && budget <= config.budget.max)) &&
    (!config.colorPalette.enabled || config.colorPalette.colorPaletteIds.includes(paletteId || -1)) &&
    (!config.planningStage.enabled || config.planningStage.planningStages.includes(planningStage || ''))
  )
}

export const useContactInfo = (quizType?: QuizType) => {
  const history = useHistory()
  const {
    setQuizScreen,
    weddingDateData,
    setWeddingDateDataState,
    referralSource,
    setReferralSourceState,
    // setNewQuizData,
    setRecordResponse,
    setRedirectUrl,
  } = useContext(QuizQuestionContext)
  const { sessionID, storeURLParameters, handleChangeAppData } = useAppContext()
  const [formError, setFormError] = useState<formError>({})
  const [loading, setLoading] = useState(false)
  const [showMetBeforePopup, setShowMetBeforePopup] = useState(false)
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null)
  const [emailWarning, setEmailWarning] = useState<string | null>(null)
  // Track whether the user has already seen a warning for this email.
  // If they submit again without changing the email, let them through.
  const warningShownForEmail = useRef<string | null>(null)

  const isEmailError = useMemo(
    () => {
      return !validate.email((weddingDateData.emailAddress ?? '').trim()) || !!formError.emailAddress
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weddingDateData.emailAddress]
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isPhoneNumberError = useMemo(
    () => Boolean(weddingDateData.phoneNumber && !isValidPhoneNumber(weddingDateData.phoneNumber.trim())),
    [weddingDateData.phoneNumber]
  )

  const formInvalid = () => {
    setFormError({
      emailAddress: isEmailError,
      phoneNumber: isPhoneNumberError,
    })
    return isEmailError || isPhoneNumberError
  }

  const handleAcceptSuggestion = useCallback(() => {
    if (emailSuggestion) {
      setWeddingDateDataState({ ...weddingDateData, emailAddress: emailSuggestion })
      setEmailSuggestion(null)
      setEmailWarning(null)
      setFormError((data) => ({ ...data, emailAddress: false }))
      warningShownForEmail.current = null
    }
  }, [emailSuggestion, weddingDateData, setWeddingDateDataState])

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setWeddingDateDataState({ ...weddingDateData, [name]: value })
    setFormError((data) => ({ ...data, [name]: false }))
    if (name === 'emailAddress') {
      setEmailSuggestion(null)
      setEmailWarning(null)
      warningShownForEmail.current = null
    }
  }

  const handleChangeReferralSource = (value: IOption | null) => {
    setReferralSourceState(value)
  }

  const handleChangePhoneNumber = (value: string) => {
    setWeddingDateDataState({ ...weddingDateData, phoneNumber: value })
    setFormError((data) => ({ ...data, phoneNumber: false }))
  }

  const handleNext = async () => {
    if (formInvalid()) return

    // Only show typo/TLD warnings once per email — if user submits again, let them through
    const emailTrimmed = (weddingDateData.emailAddress ?? '').trim()
    const alreadyWarned = warningShownForEmail.current === emailTrimmed.toLowerCase()

    if (!alreadyWarned) {
      const typoSuggestion = getEmailTypoSuggestion(emailTrimmed)
      if (typoSuggestion) {
        setEmailSuggestion(typoSuggestion)
        warningShownForEmail.current = emailTrimmed.toLowerCase()
        return
      }

      if (!isKnownTld(emailTrimmed)) {
        setEmailWarning("This email domain doesn't look quite right. Please double-check.")
        warningShownForEmail.current = emailTrimmed.toLowerCase()
        return
      }
    }

    setEmailSuggestion(null)
    setEmailWarning(null)
    setLoading(true)
    handleChangeAppData({ countLoadingTestimonial: 1 })

    // #region process local storage data
    // add it to local first
    const oldTempData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    let oldQuizResponses = oldTempData?.quizResponses || []
    if (oldQuizResponses.length > 0) {
      const refIndex = oldQuizResponses.findIndex((data: QuizResponse) => data.key === 'REFERRAL_SOURCE')
      if (refIndex > -1) {
        oldQuizResponses[refIndex].values = referralSource?.value || ''
      } else {
        oldQuizResponses.push({
          key: 'REFERRAL_SOURCE',
          values: referralSource?.value || '',
        })
      }
    } else {
      oldQuizResponses = [{ key: 'REFERRAL_SOURCE', values: referralSource?.value || '' }]
    }
    localStorage.setItem(
      'tempPoppySessionData',
      JSON.stringify({
        ...oldTempData,
        customerInfo: {
          ...oldTempData.customerInfo,
          emailAddress: weddingDateData?.emailAddress?.trim() || '',
          phoneNumber: weddingDateData?.phoneNumber?.trim() || '',
        },
        quizResponses: oldQuizResponses,
      })
    )
    // #endregion

    // #region process to the submit logic
    // #region creating payload for handleNewQuizDataRaw
    const currentTempPoppyData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const currentQuizResponsesFromLocal = currentTempPoppyData?.quizResponses || null
    const customerInfoFromLocal = currentTempPoppyData?.customerInfo || null
    const eventVenuesFromLocal = currentTempPoppyData?.eventVenues || null
    const colorPaletteFromLocal = currentTempPoppyData?.colorPalette || null
    const colorPaletteIdFromLocal = colorPaletteFromLocal?.id

    const customerInfo = {
      emailAddress: weddingDateData.emailAddress?.trim() || '',
      firstName: weddingDateData.firstName?.trim() || customerInfoFromLocal.firstName?.trim() || '',
      lastName: weddingDateData.lastName?.trim() || customerInfoFromLocal.lastName?.trim() || '',
      partnerName: weddingDateData.partnerName?.trim() || customerInfoFromLocal.partnerName?.trim() || '',
      phoneNumber: weddingDateData.phoneNumber?.trim() || customerInfoFromLocal.phoneNumber?.trim() || '',
    }
    const eventVenues = eventVenuesFromLocal || []
    const quizQuestion = 'CONTACT_INFO'
    const quizResponses = currentQuizResponsesFromLocal
    // The stored value is a raw uuid string, not JSON — JSON.parse here threw before handleNext's
    // try block whenever the context sessionID was empty, leaving the button stuck in loading.
    const sessionId = sessionID || localStorage.getItem(SESSION_NAME_LOCAL) || ''

    const payload = {
      customerInfo,
      eventVenues,
      experiment: [],
      quizQuestion,
      quizResponses,
      sessionId,
      colorPaletteId: colorPaletteIdFromLocal,
    }
    // #endregion

    try {
      // get data for API
      const { quizDataPayload, gaQuestionEvents } = handleNewQuizDataRaw(sessionID, payload)
      gaQuestionCompletedEvent(gaQuestionEvents)

      const skipServiceability = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}').skipServiceability
      const newPayloadData = {
        urlParameters: storeURLParameters ?? undefined,
        skipServiceability,
        ...quizDataPayload,
      }
      // check and direct invalidation
      const isValidTempData = checkInvalidation(newPayloadData)
      if (!isValidTempData) {
        setLoading(false)
        handleChangeAppData({ countLoadingTestimonial: -1 })
        return
      }
      // ready to call API
      const recordResponse: any = await handleLastStepSubmit(newPayloadData)

      if (recordResponse?.data?.recordResult) {
        setRecordResponse(recordResponse.data.recordResult)
      }
      // if (recordResponse?.data?.proposal?.hadProposal) {
      //   history.push({ pathname: PATH_URL.EXISTING_PROPOSAL })
      //   setQuizScreen(SCREEN_QUIZ_QUESTION.MET_BEFORE)
      //   return
      // }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      handleChangeAppData({ countLoadingTestimonial: -1 })
    }
  }

  const handleBack = async () => {
    if (window.history.length > 1) {
      history.goBack()
    } else {
      history.push({ pathname: PATH_URL.NEW_BUDGET_PAGE })
      setQuizScreen(SCREEN_QUIZ_QUESTION.NEW_BUDGET_PAGE)
    }
  }

  const gTagSetUserData = useCallback((param: any) => {
    const report = (window as { [key: string]: any })['gtag']
    if (report) {
      report('set', 'user_data', param)
    }
  }, [])

  const tracking = async (trackValue: number) => {
    // Identify user for TikTok and Bing
    const userEmail = weddingDateData.emailAddress?.trim() || ''
    const userPhone = weddingDateData.phoneNumber?.trim() || ''

    // TikTok user identification
    tiktokIdentify(userEmail)

    // Bing user identification and conversion tracking
    try {
      bingIdentify(userEmail, userPhone)
    } catch (error) {
      console.error('Error identifying user to Bing:', error)
    }

    const gAdwords = (param: any) =>
      new Promise((rs) => {
        const report = (window as { [key: string]: any })['gtag_report_conversion']
        // We report adwords conversions by inserting a function into the <head> of the page.
        if (report) {
          report({ ...param, event_callback: rs })
          setTimeout(rs, MAX_TIME_GOOGLE_TRACKING)
        } else {
          rs(true)
        }
      })
    const ga4Event = (action: ACTION_APP, params: any = {}) =>
      new Promise((rs) => {
        ga4EventApp(action, { ...params, event_callback: rs })
        setTimeout(rs, MAX_TIME_TRACKING)
      })
    const _segmentTracking = (action: string) =>
      new Promise((rs) => {
        // Fall back to the callback/timeout race only when the beacon could not be queued —
        // never fire both for the same event.
        if (segmentTrackingBeacon(action)) {
          rs(true)
          return
        }
        segmentTracking(action, {}, {}, rs)
        setTimeout(rs, MAX_TIME_TRACKING)
      })
    const pintrk = () =>
      new Promise((rs) => {
        const windowGlobal = window as any
        if (windowGlobal.pintrk) {
          windowGlobal.pintrk('track', 'lead', { value: trackValue }, function (didInit: boolean, error: unknown) {
            if (!didInit) {
              console.log(error)
            }
            rs(true)
          })
        }
        setTimeout(rs, MAX_TIME_TRACKING)
      })
    console.log({ trackValue })
    console.log({ sessionID })
    // Cheat: timeout for lib don't support callback
    const funcNoCallback = () =>
      new Promise((rs) => {
        gaEventApp(ACTION_APP.QUIZ_SUBMITTED)
        gaEventApp(ACTION_APP.FULLY_QUALIFIED_LEAD)
        fbEvent(EVENTS_FB_PIXEL.QUIZ2COMPLETE, {
          value: trackValue,
          currency: 'USD',
        })
        vwoEvent()
        setTimeout(rs, MAX_TIME_TRACKING)
      })
    const promiseFunc = [
      // transport_type hands the conversion ping to the browser so it survives the redirect;
      // the event_callback race is unchanged.
      gAdwords({
        value: trackValue,
        currency: 'USD',
        send_to: `${ENV.REACT_APP_GOOGLE_AW_CONVERSION}/${ENV.REACT_APP_GOOGLE_AW_CONVERSION_COMPLETE_QUIZ_EVENT_ID}`,
        transport_type: 'beacon',
      }),
      gAdwords({
        value: trackValue,
        currency: 'USD',
        send_to: `${ENV.REACT_APP_GOOGLE_AW_CONVERSION_2}/${ENV.REACT_APP_GOOGLE_AW_CONVERSION_LEAD_ID}`,
        transport_type: 'beacon',
      }),
      ga4Event(ACTION_APP.QUIZ_SUBMITTED),
      ga4Event(ACTION_APP.FULLY_QUALIFIED_LEAD),
      _segmentTracking(SegmentAction.SQ_QUIZ_SUBMITTED),
      _segmentTracking(SegmentAction.SQ_FULLY_QUALIFIED_LEAD),
      pintrk(),
      funcNoCallback(),
      // Add TikTok form submission conversion
      new Promise((rs) => {
        const sessionId = sessionID || localStorage.getItem(SESSION_NAME_LOCAL) || ''
        tiktokEvent(EVENTS_TIKTOK_PIXEL.SUBMIT_FORM, {
          value: trackValue,
          currency: 'USD',
          event_id: createHash('sha256').update(sessionId.toLowerCase().trim()).digest('hex'),
        })
        setTimeout(rs, MAX_TIME_TRACKING)
      }),
      // Add Bing Ads conversion tracking
      new Promise((rs) => {
        try {
          bingTrackConversion(trackValue)
        } catch (error) {
          console.error('Error tracking Bing conversion:', error)
        }
        setTimeout(rs, MAX_TIME_TRACKING)
      }),
    ]
    await Promise.all(promiseFunc)
  }

  const handleLastStepSubmit = async (payload: ISubmitQuizQuestionPayload) => {
    try {
      const calculateStyleResult: API<ISubmitQuizQuestionResponse> = await submitLastQuizResponseAPI(payload)

      // if the API return hadProposal === true, open the Met Before Popup
      if (calculateStyleResult?.data?.proposal?.hadProposal || calculateStyleResult?.data?.hadProposal === true) {
        // history.push({ pathname: PATH_URL.EXISTING_PROPOSAL })
        // setQuizScreen(SCREEN_QUIZ_QUESTION.MET_BEFORE)
        setShowMetBeforePopup(true)
        handleChangeAppData({ countLoadingTestimonial: -1 })
        return
      } else {
        setShowMetBeforePopup(false)
      }

      const paletteId = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')?.colorPalette?.id || 0
      const budgetValue = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')?.quizResponses?.find(
        (data: QuizResponse) => data.key === 'BUDGET'
      )?.values
      const planningStage = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')?.quizResponses?.find(
        (data: QuizResponse) => data.key === 'PLANNING_STAGE'
      )?.values

      const showAfterQuiz = getShowGuineaPig(
        budgetValue,
        planningStage,
        +paletteId,
        calculateStyleResult.data.guineaPigConfig?.value
      )

      const leadValueConfig = calculateStyleResult.data.leadValueConfig
      const leadValuesByBudget = leadValueConfig?.leadValuesByBudget?.sort((a, b) => a.budget - b.budget) || []
      const leadValueToIndex = leadValuesByBudget.findIndex((l) => l.budget >= +budgetValue)
      const leadValueFrom = leadValueToIndex > 0 ? leadValuesByBudget[leadValueToIndex - 1] : undefined
      const leadValueTo = leadValueToIndex >= 0 ? leadValuesByBudget[leadValueToIndex] : undefined
      let leadValue: number | undefined
      if (leadValueFrom && leadValueTo) {
        const percent = (+budgetValue - leadValueFrom.budget) / (leadValueTo.budget - leadValueFrom.budget)
        if (leadValueFrom.value > leadValueTo.value) {
          leadValue = +(leadValueFrom.value - (leadValueFrom.value - leadValueTo.value) * percent)
        } else {
          leadValue = +((leadValueTo.value - leadValueFrom.value) * percent + leadValueFrom.value)
        }
      }

      const tempPoppyDataFromLocal = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
      const customerFromLocal = tempPoppyDataFromLocal?.customerInfo || null

      if (calculateStyleResult.data.proposalUrl) {
        try {
          gTagSetUserData({
            email: customerFromLocal.emailAddress || '',
            address: {
              firstName: customerFromLocal.firstName || '',
              lastName: customerFromLocal.lastName || '',
            },
          })
          await tracking(leadValue || 0)
          console.log('tracking success')
        } catch (error) {}
        if (showAfterQuiz) {
          localStorage.setItem('tempPoppySessionData', '{}')
          setRedirectUrl(calculateStyleResult.data.proposalUrl)
          setQuizScreen(SCREEN_QUIZ_QUESTION.AFTER_QUIT_SUBMIT)
        } else {
          localStorage.setItem(SESSION_NAME_LOCAL, '')
          localStorage.setItem('tempPoppySessionData', '{}')
          window.location.href = calculateStyleResult.data.proposalUrl
        }
      } else {
        if (showAfterQuiz) {
          localStorage.setItem('tempPoppySessionData', '{}')
          setRedirectUrl(calculateStyleResult?.data?.redirectLink || `https://www.poppyflowers.com/`)
          setQuizScreen(SCREEN_QUIZ_QUESTION.AFTER_QUIT_SUBMIT)
        } else {
          localStorage.setItem(SESSION_NAME_LOCAL, '')
          localStorage.setItem('tempPoppySessionData', '{}')
          window.location.href = calculateStyleResult?.data?.redirectLink || `https://www.poppyflowers.com/`
        }
      }
      // Cheat: Single loading state during quiz submission
      // handleChangeAppData({ countLoadingTestimonial: -1 })
      if (showAfterQuiz) {
        handleChangeAppData({ countLoadingTestimonial: -1 })
      }
    } catch (err: unknown) {
      const error = err as { data: { message: string; code: string } }
      console.log('[handleSubmit]', error)
      handleChangeAppData({ countLoadingTestimonial: -1 })
      alert(error?.data?.message ?? 'Submit failed')
      if (error.data && error.data.code === 'SESSION_DOES_NOT_EXIST') {
        history.push(PATH_URL.SWIPING)
      }
    }
  }

  const handleCloseMetBeforePopup = () => {
    setShowMetBeforePopup(false)
    setEmailSuggestion(null)
    setEmailWarning(null)
    warningShownForEmail.current = null

    setWeddingDateDataState({
      emailAddress: '',
      phoneNumber: '',
      weddingDate: weddingDateData.weddingDate || '',
      fullName: weddingDateData.fullName || '',
      partnerName: weddingDateData.partnerName || '',
    })
    setReferralSourceState(null)
    setFormError({
      emailAddress: false,
      phoneNumber: false,
    })
    setLoading(false)

    const tempDataFromLocal = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    if (tempDataFromLocal.customerInfo) {
      tempDataFromLocal.customerInfo.emailAddress = ''
      tempDataFromLocal.customerInfo.phoneNumber = ''
    }
    localStorage.setItem('tempPoppySessionData', JSON.stringify(tempDataFromLocal))
  }

  const nextDisabled = useMemo(() => {
    return !weddingDateData || weddingDateData.emailAddress === undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weddingDateData])

  useEffect(() => {
    const tempDataFromLocal = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const customerInfoFromLocal = tempDataFromLocal?.customerInfo || null
    if (customerInfoFromLocal) {
      setWeddingDateDataState({
        ...weddingDateData,
        emailAddress: customerInfoFromLocal?.emailAddress || '',
        phoneNumber: customerInfoFromLocal?.phoneNumber || '',
      })
    }
    const quizResponsesFromLocal = tempDataFromLocal?.quizResponses || null
    const refIndex = quizResponsesFromLocal?.findIndex((data: QuizResponse) => data.key === 'REFERRAL_SOURCE')
    if (refIndex > -1) {
      const refData = REFERRAL_SOURCES.find((e) => e.value === quizResponsesFromLocal[refIndex].values)
      if (refData) {
        setReferralSourceState(refData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkInvalidation = (tempData: any) => {
    console.log('tempData', tempData)
    if (!tempData?.customerInfo) {
      toast.error('Invalid customer info.\nPlease enter your email address.')
      return false
    }
    if (!tempData?.sessionId) {
      toast.error('Invalid session.\nPlease complete all steps before submitting your quiz.')
      return false
    }
    if (!tempData?.quizResponses) {
      toast.error('Invalid quiz responses.\nPlease complete all steps before submitting your quiz.')
      return false
    }
    if (!tempData?.eventVenues || tempData?.eventVenues?.length === 0) {
      toast.error('Invalid event venues.\nPlease complete all steps before submitting your quiz.')
      return false
    }
    return true
  }

  return {
    weddingDateData,
    handleNext,
    handleBack,
    formError,
    handleChangeInput,
    referralSource,
    handleChangeReferralSource,
    handleChangePhoneNumber,
    nextDisabled,
    loading,
    showMetBeforePopup,
    handleCloseMetBeforePopup,
    emailSuggestion,
    emailWarning,
    handleAcceptSuggestion,
  }
}
